import { createBrowserRouter, Link, Navigate, type RouteObject } from 'react-router'
import { RequireAuth } from '../auth/RequireAuth'
import { ErrorBoundary } from './ErrorBoundary'
import { Button } from '../design/components'
import { Placeholder } from '../routes/Placeholder'
import { CampaignDetailPage } from '../routes/campaigns/CampaignDetailPage'
import { CampaignListPage } from '../routes/campaigns/CampaignListPage'
import { CityPage } from '../routes/campaigns/CityPage'
import { ComicDetailPage } from '../routes/comics/ComicDetailPage'
import { ComicListPage } from '../routes/comics/ComicListPage'
import { CharacterListPage } from '../routes/characters/CharacterListPage'
import { CharacterPrintPage } from '../routes/characters/CharacterPrintPage'
import { CharacterSheetPage } from '../routes/characters/CharacterSheetPage'
import { CaseDetailPage } from '../routes/play/CaseDetailPage'
import { CaseListPage } from '../routes/play/CaseListPage'
import { EncounterDetailPage } from '../routes/play/EncounterDetailPage'
import { EncounterListPage } from '../routes/play/EncounterListPage'
import { SceneLandingPage } from '../routes/play/SceneLandingPage'
import { SceneTrackerPage } from '../routes/play/SceneTrackerPage'
import { CharacterImagesPage } from '../routes/media/CharacterImagesPage'
import { PortraitComposerPage } from '../routes/media/PortraitComposerPage'
import { LocationImagesPage } from '../routes/media/LocationImagesPage'
import { LocationComposerPage } from '../routes/media/LocationComposerPage'
import { LiveSessionListPage } from '../routes/live/LiveSessionListPage'
import { LiveSessionPage } from '../routes/live/LiveSessionPage'
import { ConfirmEmailPage } from '../routes/auth/ConfirmEmailPage'
import { LoginPage } from '../routes/auth/LoginPage'
import { RegisterPage } from '../routes/auth/RegisterPage'
import { Gallery } from '../routes/dev/Gallery'
import { PrintPreview } from '../routes/dev/PrintPreview'

/**
 * One screen, behind the auth guard and inside its own error boundary.
 *
 * **The boundary is per route rather than only at the root.** A single boundary around the
 * whole app turns one screen's bug into a blank window with no header and no navigation —
 * at a table, mid-session, on a phone. Per route, a broken sheet costs the sheet: the tabs
 * still work, every other screen still works, and the escape link goes somewhere that
 * renders. `key` is the path, so navigating away from a crashed screen mounts a fresh
 * boundary rather than carrying the old error into the new route.
 */
function protectedRoute(path: string, element: React.ReactNode): RouteObject {
  return {
    path,
    element: (
      <ErrorBoundary
        key={path}
        what="This screen did not load"
        escape={
          <Link to="/characters">
            <Button variant="secondary" fullWidth>
              Back to your characters
            </Button>
          </Link>
        }
      >
        <RequireAuth>{element}</RequireAuth>
      </ErrorBoundary>
    ),
  }
}

/**
 * Every route in the app.
 *
 * `/dev/gallery` is registered only in a dev build, and deliberately outside the
 * auth guard — it is the design system's test gate and depends on no API call.
 */
const routes: RouteObject[] = [
  { path: '/', element: <Navigate to="/sheet" replace /> },

  // ---- public ----
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  { path: '/confirm-email', element: <ConfirmEmailPage /> },

  // ---- authenticated ----
  // The Sheet tab lands on the roster; a sheet is always a specific character.
  { path: '/sheet', element: <Navigate to="/characters" replace /> },
  protectedRoute('/characters', <CharacterListPage />),
  protectedRoute('/characters/:id', <CharacterSheetPage />),
  // The gallery hangs off a sheet rather than living on it: a sheet is already fourteen
  // sections long at 390px, and the gallery is a screen's worth on its own. The advisor
  // used to sit beside it and no longer has a route at all — Bob is the shell's dock now,
  // reachable from every screen rather than from one character's sheet.
  protectedRoute('/characters/:id/images', <CharacterImagesPage />),
  // The printable sheet is a route rather than a dialog, and deliberately its own document:
  // it renders outside the shell, so what the print dialog previews is the page itself. A
  // route also means it survives a middle-click, a bookmark and a reload at the table.
  protectedRoute('/characters/:id/print', <CharacterPrintPage />),
  // The composer is its own screen rather than a section of the gallery: it is a working
  // surface with its own prompt, its own settings and its own unsaved results, and folding
  // that into a page whose job is "here are this character's pictures" would bury both.
  protectedRoute('/characters/:id/portrait', <PortraitComposerPage />),
  // The Scene tab lands on the scene this device last opened — device-local, because
  // "the scene in play" is a fact about this phone at this table and the API has no
  // concept of it. With none, an empty state rather than a redirect nobody asked for.
  protectedRoute('/scene', <SceneLandingPage />),
  protectedRoute('/scenes/:sceneId', <SceneTrackerPage />),

  // The City tab lands on the campaign list, for the reason the Sheet tab lands on the
  // character roster: a city belongs to one campaign and the app has no "current" one.
  { path: '/city', element: <Navigate to="/campaigns" replace /> },
  protectedRoute('/campaigns', <CampaignListPage />),
  protectedRoute('/campaigns/:id', <CampaignDetailPage />),
  protectedRoute('/campaigns/:id/city', <CityPage />),

  // Cases and encounters hang off a campaign; the scene and encounter themselves are
  // addressed directly, exactly as the API addresses them.
  protectedRoute('/campaigns/:id/cases', <CaseListPage />),
  protectedRoute('/cases/:caseId', <CaseDetailPage />),
  protectedRoute('/campaigns/:id/locations/:locationId/images', <LocationImagesPage />),
  // The location composer is its own screen for the reason the portrait one is: a working
  // surface with its own prompt, settings and unsaved results does not belong folded into a
  // page whose job is "here are this place's pictures". The image type arrives as `?type=`,
  // chosen by whichever button on the gallery was pressed.
  protectedRoute('/campaigns/:id/locations/:locationId/compose', <LocationComposerPage />),
  protectedRoute('/campaigns/:id/encounters', <EncounterListPage />),
  protectedRoute('/encounters/:encounterId', <EncounterDetailPage />),

  // Live sessions are addressed directly rather than under a campaign: a player arrives at one
  // from a banner or the Live tab, and in neither case do they know or care which campaign it is
  // until the screen tells them.
  protectedRoute('/live', <LiveSessionListPage />),
  protectedRoute('/live/:sessionId', <LiveSessionPage />),

  // A comic draws one case, and is addressed directly once opened — same cut as cases.
  protectedRoute('/campaigns/:id/comics', <ComicListPage />),
  protectedRoute('/comics/:comicId', <ComicDetailPage />),

  {
    path: '*',
    element: (
      <Placeholder
        title="Lost"
        subtitle="NO SUCH PAGE"
        stage="404"
        what="That address does not lead anywhere in this app."
      />
    ),
  },
]

if (import.meta.env.DEV) {
  routes.splice(
    routes.length - 1,
    0,
    { path: '/dev/gallery', element: <Gallery /> },
    // The printed sheet's equivalent of the gallery: the A4 layout on the captured fixture,
    // with no API, no sign-in and no seeded character between a reviewer and the page.
    { path: '/dev/print', element: <PrintPreview /> },
  )
}

export const router = createBrowserRouter(routes)
